import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { FirmInfoSettings } from './FirmInfoSettings';
import { DocumentTemplateSettings } from './DocumentTemplateSettings';
import { OAuthSettings } from './OAuthSettings';
import { PeppolSettings } from './PeppolSettings';
import { ThemeSettings } from './ThemeSettings';
import api from '@/lib/api';
import { Firm } from '@/types/settings';
import { 
  Building2, 
  FileText, 
  Mail, 
  LayoutDashboard,
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Users,
  Calendar,
  Hash,
  Globe,
  CreditCard,
  Palette,
  ScrollText,
} from 'lucide-react';

interface FirmSettingsProps {
  firmId: string;
  canModify: boolean;
}

interface OverviewData {
  name: string;
  email: string;
  phone: string;
  country: string;
  invoiceNumber: number;
  invoiceYear: number;
  financialYearStartDate: string | null;
  templates: {
    invoice: boolean;
    creditNote: boolean;
    downPayment: boolean;
  };
  oauth: {
    enabled: boolean;
    hasToken: boolean;
    tokenValid: boolean;
    expiresAt: string | null;
  };
  subscription: {
    startDate: string | null;
    endDate: string | null;
    maxUsers: number | null;
    currentUsers: number;
    price: number | null;
  };
}

export function FirmSettings({ firmId, canModify }: FirmSettingsProps) {
  const [firm, setFirm] = useState<Firm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchFirmData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/firms/${firmId}/`);
      setFirm(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to load firm settings');
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => {
    fetchFirmData();
  }, [fetchFirmData]);

  const refreshFirmData = useCallback(() => {
    fetchFirmData();
  }, [fetchFirmData]);

  // Loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!firm) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertDescription>Firm data not found.</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Build overview data from firm
  const overview: OverviewData = {
    name: firm.name,
    email: firm.email,
    phone: firm.phone,
    country: firm.country,
    invoiceNumber: firm.invoice_number,
    invoiceYear: firm.invoice_year,
    financialYearStartDate: firm.financial_year_start_date || null,
    templates: {
      invoice: firm.template_status.invoice_template.uploaded,
      creditNote: firm.template_status.credit_note_template.uploaded,
      downPayment: firm.template_status.down_payment_template.uploaded,
    },
    oauth: {
      enabled: firm.oauth_status.oauth_enabled,
      hasToken: firm.oauth_status.has_access_token,
      tokenValid: firm.oauth_status.has_access_token && firm.oauth_status.has_refresh_token,
      expiresAt: firm.oauth_status.token_expires_at || null,
    },
    subscription: {
      startDate: firm.contract_start_date || null,
      endDate: firm.contract_end_date || null,
      maxUsers: firm.max_users ?? null,
      currentUsers: firm.user_count || 0,
      price: firm.subscription_price ?? null,
    },
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 h-11 p-1">
          <TabsTrigger 
            value="overview" 
            className="flex items-center gap-2"
          >
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger 
            value="firm-info" 
            className="flex items-center gap-2"
          >
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Firm Settings</span>
          </TabsTrigger>
          <TabsTrigger 
            value="templates" 
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Templates</span>
          </TabsTrigger>
          <TabsTrigger 
            value="email" 
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
          <TabsTrigger 
            value="peppol" 
            className="flex items-center gap-2"
          >
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">Peppol</span>
          </TabsTrigger>
          <TabsTrigger 
            value="theme" 
            className="flex items-center gap-2"
          >
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Theme</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab overview={overview} onTabChange={setActiveTab} />
        </TabsContent>

        <TabsContent value="firm-info" className="mt-6">
          <FirmInfoSettings 
            firm={firm} 
            canModify={canModify}
            onFirmUpdate={refreshFirmData}
          />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <DocumentTemplateSettings 
            firm={firm}
            canModify={canModify}
            onUpdate={refreshFirmData}
          />
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <OAuthSettings 
            firm={firm}
            canModify={canModify}
            onUpdate={refreshFirmData}
          />
        </TabsContent>

        <TabsContent value="peppol" className="mt-6">
          <PeppolSettings 
            firmId={firmId}
            canModify={canModify}
          />
        </TabsContent>

        <TabsContent value="theme" className="mt-6">
          <ThemeSettings 
            firm={firm}
            canModify={canModify}
            onFirmUpdate={refreshFirmData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Loading skeleton component
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-12 flex-1" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-48" />
        ))}
      </div>
    </div>
  );
}

// Overview tab component
interface OverviewTabProps {
  overview: OverviewData;
  onTabChange: (tab: string) => void;
}

function OverviewTab({ overview, onTabChange }: OverviewTabProps) {
  const templateCount = [
    overview.templates.invoice,
    overview.templates.creditNote,
    overview.templates.downPayment,
  ].filter(Boolean).length;

  const formatFinancialYearStart = (date: string | null): string => {
    if (!date) return 'January 1';
    const [month, day] = date.split('-');
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex] || 'January'} ${parseInt(day, 10) || 1}`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Firm Card */}
        <Card 
          className="cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => onTabChange('firm-info')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <Badge variant="outline">Firm</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold truncate">{overview.name}</p>
            <p className="text-sm text-muted-foreground truncate">{overview.email}</p>
          </CardContent>
        </Card>

        {/* Numbering Card */}
        <Card 
          className="cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => onTabChange('firm-info')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Hash className="h-5 w-5 text-muted-foreground" />
              <Badge variant="outline">Invoicing</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">Invoice #{overview.invoiceNumber}</p>
            <p className="text-sm text-muted-foreground">Year: {overview.invoiceYear}</p>
          </CardContent>
        </Card>

        {/* Templates Card */}
        <Card 
          className="cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => onTabChange('templates')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <Badge variant={templateCount === 3 ? 'default' : 'secondary'}>
                {templateCount}/3
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">Templates</p>
            <p className="text-sm text-muted-foreground">
              {templateCount === 3 ? 'All uploaded' : `${3 - templateCount} missing`}
            </p>
          </CardContent>
        </Card>

        {/* Email Card */}
        <Card 
          className="cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => onTabChange('email')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <Badge
                variant={overview.oauth.tokenValid ? 'default' : 'destructive'}
                className={overview.oauth.tokenValid ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' : ''}
              >
                {overview.oauth.tokenValid ? 'Connected' : 'Not connected'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">Email</p>
            <p className="text-sm text-muted-foreground">
              {overview.oauth.enabled ? 'OAuth enabled' : 'Configure email'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Detailed Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Firm Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-primary" />
              Firm Information
            </CardTitle>
            <CardDescription>Basic firm details and contact information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <InfoRow icon={<Building2 className="h-4 w-4" />} label="Name" value={overview.name} />
            <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={overview.email} />
            <InfoRow icon={<Globe className="h-4 w-4" />} label="Country" value={overview.country} />
            <InfoRow 
              icon={<Calendar className="h-4 w-4" />} 
              label="Financial Year Start" 
              value={formatFinancialYearStart(overview.financialYearStartDate)} 
            />
          </CardContent>
        </Card>

        {/* Document Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-primary" />
              Document Templates
            </CardTitle>
            <CardDescription>Template upload status for documents</CardDescription>
          </CardHeader>
          <CardContent>
            <TemplateStatusRow 
              label="Invoice Template" 
              uploaded={overview.templates.invoice} 
            />
            <TemplateStatusRow 
              label="Credit Note Template" 
              uploaded={overview.templates.creditNote} 
            />
            <TemplateStatusRow 
              label="Down Payment Template" 
              uploaded={overview.templates.downPayment} 
            />
          </CardContent>
        </Card>

        {/* Email Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-primary" />
              Email Configuration
            </CardTitle>
            <CardDescription>Microsoft OAuth integration status</CardDescription>
          </CardHeader>
          <CardContent>
            <StatusRow 
              label="OAuth Enabled" 
              status={overview.oauth.enabled} 
            />
            <StatusRow 
              label="Access Token" 
              status={overview.oauth.hasToken} 
            />
            <StatusRow 
              label="Connection Valid" 
              status={overview.oauth.tokenValid} 
            />
            {overview.oauth.expiresAt && (
              <div className="flex items-center justify-between py-2.5 border-b last:border-b-0">
                <span className="text-sm text-muted-foreground">Token Expires</span>
                <span className="text-sm font-medium">
                  {new Date(overview.oauth.expiresAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Info */}
        {overview.subscription.startDate && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5 text-primary" />
                Subscription
              </CardTitle>
              <CardDescription>Your Sentra subscription details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <InfoBlock 
                  icon={<Calendar className="h-4 w-4" />}
                  label="Start Date" 
                  value={overview.subscription.startDate} 
                />
                <InfoBlock 
                  icon={<Calendar className="h-4 w-4" />}
                  label="End Date" 
                  value={overview.subscription.endDate || 'Not set'} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InfoBlock 
                  icon={<Users className="h-4 w-4" />}
                  label="Users" 
                  value={`${overview.subscription.currentUsers}${overview.subscription.maxUsers ? ` / ${overview.subscription.maxUsers}` : ' (unlimited)'}`} 
                />
                {overview.subscription.price && (
                  <InfoBlock 
                    icon={<CreditCard className="h-4 w-4" />}
                    label="Price" 
                    value={`€${overview.subscription.price}`} 
                  />
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Advanced / audit — kept deep in the page because most users
          never need it and it's intimidating to surface in nav. */}
      <div className="pt-4">
        <Card className="border-dashed">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <ScrollText className="h-5 w-5 text-muted-foreground" />
              <Badge variant="outline">Advanced</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-base font-semibold">Activity log</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Append-only record of every edit, deletion, login and
              reconciliation event in the firm. Soft-deleted rows can be
              restored from here.
            </p>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="mt-3"
            >
              <a href="/audit/activity">Open activity log</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Helper components
function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-b-0">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        {label}
      </div>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function InfoBlock({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function TemplateStatusRow({ label, uploaded }: { label: string; uploaded: boolean }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-b-0">
      <span className="text-sm font-medium">{label}</span>
      {uploaded ? (
        <Badge className="gap-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          Uploaded
        </Badge>
      ) : (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          Missing
        </Badge>
      )}
    </div>
  );
}

function StatusRow({ label, status }: { label: string; status: boolean }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-b-0">
      <span className="text-sm font-medium">{label}</span>
      {status ? (
        <Badge variant="default" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Yes
        </Badge>
      ) : (
        <Badge variant="secondary" className="gap-1">
          <XCircle className="h-3 w-3" />
          No
        </Badge>
      )}
    </div>
  );
}
